import { SiteActionTypes, SiteActionsUnion } from '../actions/site.actions';
import {
	Site,
	Team,
	Vendor,
	Folder,
	HearingProtectionType,
	IsolationNotVerifiedReason,
	OrificeSize,
	LeakType,
	LeakStatus,
	Fitting,
	FittingSize,
	InsulationType,
	SteamCost,
	SiteName,
	PlumeLength,
	UnitPreferences,
} from '../../site.interfaces';
import { orderByProperty, orderReverseByProperty } from 'src/app/shared/utils';

export interface SiteState {
	site: Site | null;
	siteNames: SiteName[] | null;
	teams: Team[] | null;
	vendors: Vendor[] | null;
	folders: Folder[] | null;
	hearingProtectionTypes: HearingProtectionType[] | null;
	isolationNotVerifiedReasons: IsolationNotVerifiedReason[] | null;
	orificeSizes: OrificeSize[];
	plumeLengths: PlumeLength[];
	leakTypes: LeakType[];
	leakStatuses: LeakStatus[];
	fittings: Fitting[];
	fittingSizes: FittingSize[];
	insulationTypes: InsulationType[];
	steamCosts: SteamCost[];
	fetchingSteamCosts: boolean;
	steamCostsSubmitting: boolean;
	error: string;
	leakFormDetailsLoading: boolean;
	siteSettingsSaving: boolean;
	siteSettingsSaved: boolean;
	unitPreferences: UnitPreferences | null;
	loading: boolean;
	loaded: boolean,
}

export const initialState: SiteState = {
	site: null,
	siteNames: null,
	teams: null,
	vendors: null,
	folders: null,
	loading: false,
	loaded: true,
	hearingProtectionTypes: null,
	isolationNotVerifiedReasons: null,
	orificeSizes: null,
	plumeLengths: null,
	leakTypes: null,
	leakStatuses: null,
	fittings: null,
	fittingSizes: null,
	insulationTypes: null,
	steamCosts: null,
	fetchingSteamCosts: false,
	steamCostsSubmitting: false,
	error: null,
	leakFormDetailsLoading: false,
	siteSettingsSaving: false,
	siteSettingsSaved: false,
	unitPreferences: null,
};

export function reducer(state: SiteState = initialState, action: SiteActionsUnion): SiteState {
	switch (action.type) {
		case SiteActionTypes.GetSite: {
			return {
				...state,
				loading: true,
				loaded: false,

			};
		}
		case SiteActionTypes.GetSiteSuccess: {
			return {
				...state,
				site: action.site,
				error: null,
			};
		}

		case SiteActionTypes.GetSiteFailure: {
			return {
				...state,
				site: null,
				error: action.error,
			};
		}

		case SiteActionTypes.GetSiteNames: {
			return {
				...state,
			};
		}

		case SiteActionTypes.GetSiteNamesSuccess: {
			return {
				...state,
				siteNames: action.siteNames,
			};
		}

		case SiteActionTypes.GetSiteNamesFailure: {
			return {
				...state,
				error: action.error,
			};
		}

		case SiteActionTypes.GetLeakFormDetailsForSite: {
			return {
				...state,
				leakFormDetailsLoading: true,
			};
		}

		case SiteActionTypes.GetLeakFormDetailsForSiteSuccess: {
			const orificeSizes = orderByProperty([...action.formDetails.orificeSizes], 'value');
			const plumeLengths = orderByProperty([...action.formDetails.plumeLengths], 'value');
			const reasons = orderByProperty([...action.formDetails.isolationNotVerifiedReasons], 'reason');
			const hearingProtectionTypes = orderReverseByProperty([...action.formDetails.hearingProtectionTypes], 'name');
			const unitPreferences = action.formDetails.unitPreferences;

			return {
				...state,
				orificeSizes: orificeSizes,
				plumeLengths: plumeLengths,
				isolationNotVerifiedReasons: reasons,
				hearingProtectionTypes: hearingProtectionTypes,
				fittings: orderByProperty([...action.formDetails.fittings], 'name'),
				fittingSizes: orderByProperty([...action.formDetails.fittingSizes], 'value'),
				folders: action.formDetails.folders,
				insulationTypes: orderByProperty([...action.formDetails.insulationTypes], 'name'),
				leakStatuses: orderByProperty([...action.formDetails.leakStatuses], 'name'),
				leakTypes: action.formDetails.leakTypes,
				teams: orderByProperty([...action.formDetails.teams], 'name'),
				vendors: orderByProperty([...action.formDetails.vendors], 'name'),
				unitPreferences: unitPreferences,
				error: null,
				leakFormDetailsLoading: false,
			};
		}

		case SiteActionTypes.GetLeakFormDetailsForSiteFailure: {
			return {
				...state,
				orificeSizes: null,
				isolationNotVerifiedReasons: null,
				hearingProtectionTypes: null,
				fittings: null,
				fittingSizes: null,
				folders: null,
				insulationTypes: null,
				leakStatuses: null,
				leakTypes: null,
				teams: null,
				vendors: null,
				error: action.error,
				leakFormDetailsLoading: false,
			};
		}

		case SiteActionTypes.GetSteamCosts: {
			return {
				...state,
				fetchingSteamCosts: true,
			};
		}

		case SiteActionTypes.GetSteamCostsSuccess: {
			return {
				...state,
				steamCosts: action.steamCosts,
				fetchingSteamCosts: false,
				error: null,
			};
		}

		case SiteActionTypes.GetSteamCostsFailure: {
			return {
				...state,
				steamCosts: null,
				fetchingSteamCosts: false,
				error: action.error,
			};
		}

		case SiteActionTypes.UpdateSteamCosts: {
			return {
				...state,
				steamCostsSubmitting: true,
			};
		}

		case SiteActionTypes.UpdateSteamCostsSuccess: {
			return {
				...state,
				steamCostsSubmitting: false,
				error: null,
			};
		}

		case SiteActionTypes.UpdateSteamCostsFailure: {
			return {
				...state,
				steamCostsSubmitting: false,
				error: action.error,
			};
		}

		case SiteActionTypes.AddFitting: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.AddFittingSuccess: {
			let fittings = state.fittings;
			fittings.push(action.fitting);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				fittings: orderByProperty(fittings, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.AddFittingFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.UpdateFitting: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.UpdateFittingSuccess: {
			let fittings = state.fittings.map(fitting => {
				if (fitting.id === action.fitting.id) {
					return { ...fitting, name: action.fitting.name };
				} else {
					return fitting;
				}
			});
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				fittings: orderByProperty(fittings, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.UpdateFittingFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.DeleteFitting: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.DeleteFittingSuccess: {
			let fittings = state.fittings.filter(fitting => fitting.id !== action.fittingId);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				fittings: orderByProperty(fittings, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.DeleteFittingFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.AddFolder: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.AddFolderSuccess: {
			let folders = state.folders;
			folders.push(action.folder);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				folders: orderByProperty(folders, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.AddFolderFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.UpdateFolder: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.UpdateFolderSuccess: {
			let folders = state.folders.map(folder => {
				if (folder.id === action.folder.id) {
					return { ...folder, name: action.folder.name };
				} else {
					return folder;
				}
			});
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				folders: orderByProperty(folders, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.UpdateFolderFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.DeleteFolder: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.DeleteFolderSuccess: {
			let folders = state.folders.filter(folder => folder.id !== action.folderId);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				folders: orderByProperty(folders, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.DeleteFolderFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.AddHearingProtectionType: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.AddHearingProtectionTypeSuccess: {
			let hearingProtectionTypes = state.hearingProtectionTypes;
			hearingProtectionTypes.push(action.hearingProtectionType);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				hearingProtectionTypes: orderByProperty(hearingProtectionTypes, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.AddHearingProtectionTypeFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.UpdateHearingProtectionType: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.UpdateHearingProtectionTypeSuccess: {
			let hearingProtectionTypes = state.hearingProtectionTypes.map(hearingProtectionType => {
				if (hearingProtectionType.id === action.hearingProtectionType.id) {
					return { ...hearingProtectionType, name: action.hearingProtectionType.name };
				} else {
					return hearingProtectionType;
				}
			});
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				hearingProtectionTypes: orderByProperty(hearingProtectionTypes, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.UpdateHearingProtectionTypeFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.DeleteHearingProtectionType: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.DeleteHearingProtectionTypeSuccess: {
			let hearingProtectionTypes = state.hearingProtectionTypes.filter(
				hearingProtectionType => hearingProtectionType.id !== action.hearingProtectionTypeId
			);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				hearingProtectionTypes: orderByProperty(hearingProtectionTypes, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.DeleteHearingProtectionTypeFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.AddInsulationType: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.AddInsulationTypeSuccess: {
			let insulationTypes = state.insulationTypes;
			insulationTypes.push(action.insulationType);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				insulationTypes: orderByProperty(insulationTypes, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.AddInsulationTypeFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.UpdateInsulationType: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.UpdateInsulationTypeSuccess: {
			let insulationTypes = state.insulationTypes.map(insulationType => {
				if (insulationType.id === action.insulationType.id) {
					return { ...insulationType, name: action.insulationType.name };
				} else {
					return insulationType;
				}
			});
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				insulationTypes: orderByProperty(insulationTypes, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.UpdateInsulationTypeFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.DeleteInsulationType: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.DeleteInsulationTypeSuccess: {
			let insulationTypes = state.insulationTypes.filter(insulationType => insulationType.id !== action.insulationTypeId);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				insulationTypes: orderByProperty(insulationTypes, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.DeleteInsulationTypeFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.AddIsoNotVerifiedReason: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.AddIsoNotVerifiedReasonSuccess: {
			let isoNotVerifiedReasons = state.isolationNotVerifiedReasons;
			isoNotVerifiedReasons.push(action.isoNotVerifiedReason);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				isolationNotVerifiedReasons: orderByProperty(isoNotVerifiedReasons, 'reason'),
				error: null,
			};
		}

		case SiteActionTypes.AddIsoNotVerifiedReasonFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.UpdateIsoNotVerifiedReason: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.UpdateIsoNotVerifiedReasonSuccess: {
			let isoNotVerifiedReasons = state.isolationNotVerifiedReasons.map(isoNotVerifiedReason => {
				if (isoNotVerifiedReason.id === action.isoNotVerifiedReason.id) {
					return { ...isoNotVerifiedReason, reason: action.isoNotVerifiedReason.reason };
				} else {
					return isoNotVerifiedReason;
				}
			});
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				isolationNotVerifiedReasons: orderByProperty(isoNotVerifiedReasons, 'reason'),
				error: null,
			};
		}

		case SiteActionTypes.UpdateIsoNotVerifiedReasonFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.DeleteIsoNotVerifiedReason: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.DeleteIsoNotVerifiedReasonSuccess: {
			let isoNotVerifiedReasons = state.isolationNotVerifiedReasons.filter(
				isoNotVerifiedReason => isoNotVerifiedReason.id !== action.isoNotVerifiedReasonId
			);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				isolationNotVerifiedReasons: orderByProperty(isoNotVerifiedReasons, 'reason'),
				error: null,
			};
		}

		case SiteActionTypes.DeleteIsoNotVerifiedReasonFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.AddStatus: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.AddStatusSuccess: {
			let statuses = state.leakStatuses;
			statuses.push(action.status);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				leakStatuses: orderByProperty(statuses, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.AddStatusFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.UpdateStatus: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.UpdateStatusSuccess: {
			let statuses = state.leakStatuses.map(status => {
				if (status.id === action.status.id) {
					return { ...status, name: action.status.name };
				} else {
					return status;
				}
			});
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				leakStatuses: orderByProperty(statuses, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.UpdateStatusFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.DeleteStatus: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.DeleteStatusSuccess: {
			let statuses = state.leakStatuses.filter(status => status.id !== action.statusId);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				leakStatuses: orderByProperty(statuses, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.DeleteStatusFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.AddTeam: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.AddTeamSuccess: {
			let teams = state.teams;
			teams.push(action.team);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				teams: orderByProperty(teams, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.AddTeamFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.UpdateTeam: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.UpdateTeamSuccess: {
			let teams = state.teams.map(team => {
				if (team.id === action.team.id) {
					return { ...team, name: action.team.name };
				} else {
					return team;
				}
			});
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				teams: orderByProperty(teams, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.UpdateTeamFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.DeleteTeam: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.DeleteTeamSuccess: {
			let teams = state.teams.filter(team => team.id !== action.teamId);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				teams: orderByProperty(teams, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.DeleteTeamFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.AddVendor: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.AddVendorSuccess: {
			let vendors = state.vendors;
			vendors.push(action.vendor);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				vendors: orderByProperty(vendors, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.AddVendorFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.UpdateVendor: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.UpdateVendorSuccess: {
			let vendors = state.vendors.map(vendor => {
				if (vendor.id === action.vendor.id) {
					return { ...vendor, name: action.vendor.name };
				} else {
					return vendor;
				}
			});
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				vendors: orderByProperty(vendors, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.UpdateVendorFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.DeleteVendor: {
			return {
				...state,
				siteSettingsSaving: true,
				siteSettingsSaved: false,
			};
		}

		case SiteActionTypes.DeleteVendorSuccess: {
			let vendors = state.vendors.filter(vendor => vendor.id !== action.vendorId);
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: true,
				vendors: orderByProperty(vendors, 'name'),
				error: null,
			};
		}

		case SiteActionTypes.DeleteVendorFailure: {
			return {
				...state,
				siteSettingsSaving: false,
				siteSettingsSaved: false,
				error: action.error,
			};
		}

		case SiteActionTypes.ClearSite: {
			return {
				...state,
				site: null,
			};
		}

		default:
			return state;
	}
}

export const getSite = (state: SiteState) => state.site;
export const getSiteNames = (state: SiteState) => state.siteNames;
export const getTeams = (state: SiteState) => state.teams;
export const getVendors = (state: SiteState) => state.vendors;
export const getFolders = (state: SiteState) => state.folders;
export const getHearingProtectionForSites = (state: SiteState) => state.hearingProtectionTypes;
export const GetIsolationNotVerifiedReasonsForSite = (state: SiteState) => state.isolationNotVerifiedReasons;
export const getOrificeSizes = (state: SiteState) => state.orificeSizes;
export const getPlumeLengths = (state: SiteState) => state.plumeLengths;
export const getFittings = (state: SiteState) => state.fittings;
export const getFittingSizes = (state: SiteState) => state.fittingSizes;
export const getInsulationTypes = (state: SiteState) => state.insulationTypes;
export const getLeakStatuses = (state: SiteState) => state.leakStatuses;
export const getLeakTypes = (state: SiteState) => state.leakTypes;
export const getSteamCosts = (state: SiteState) => state.steamCosts;
export const getSteamCostsLoading = (state: SiteState) => state.fetchingSteamCosts;
export const getSteamCostsSubmitting = (state: SiteState) => state.steamCostsSubmitting;
export const getLeakFormDetailsLoading = (state: SiteState) => state.leakFormDetailsLoading;
export const getSiteSettingsSaving = (state: SiteState) => state.siteSettingsSaving;
export const getSiteSettingsSaved = (state: SiteState) => state.siteSettingsSaved;
export const getError = (state: SiteState) => state.error;
export const getUnitPreferences = (state: SiteState) => state.unitPreferences;
